import { useState, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Props for item codes input component with autocomplete
interface ItemCodesInputProps {
  value: string[];
  onChange: (codes: string[]) => void;
  suggestions: string[];
}

export const ItemCodesInput = ({ value, onChange, suggestions }: ItemCodesInputProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter suggestions by input and exclude already selected codes
  const filteredSuggestions = suggestions.filter(
    (suggestion) =>
      suggestion.toLowerCase().includes(inputValue.toLowerCase()) &&
      !value.includes(suggestion)
  );

  // Add a new item code if it's unique
  const addItemCode = (code: string) => {
    const trimmedCode = code.trim();
    if (trimmedCode && !value.includes(trimmedCode)) {
      const newCodes = [...value, trimmedCode];
      console.log('[ItemCodesInput] Adding code:', trimmedCode, '| New array:', newCodes);
      onChange(newCodes);
      setInputValue('');
      setOpen(false);
      inputRef.current?.focus();
    }
  };

  // Remove an item code from the list
  const removeItemCode = (codeToRemove: string) => {
    const newCodes = value.filter((code) => code !== codeToRemove);
    console.log('[ItemCodesInput] Removing code:', codeToRemove, '| New array:', newCodes);
    onChange(newCodes);
  };

  // Handle Enter key to add code
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addItemCode(inputValue);
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="item-codes">{t('editor.itemCodes')}</Label>
      <div className="space-y-2">
        <Popover open={open && filteredSuggestions.length > 0} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <div className="relative">
              <Input
                ref={inputRef}
                id="item-codes"
                type="text"
                placeholder={t('editor.itemCodesPlaceholder')}
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  setOpen(e.target.value.length > 0);
                }}
                onKeyDown={handleKeyDown}
                onFocus={() => setOpen(inputValue.length > 0)}
                className="h-touch"
              />
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="max-h-60 overflow-auto">
              {filteredSuggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors first:rounded-t-md last:rounded-b-md"
                  onClick={() => addItemCode(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {value.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {value.map((code) => (
              <Badge key={code} variant="secondary" className="gap-1 pr-1">
                {code}
                <button
                  type="button"
                  onClick={() => removeItemCode(code)}
                  className="ml-1 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                  aria-label={`Remove ${code}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
